// Scout state derivation, ported from triangula-commander/src/util/scoutUtil.ts and util.ts so the
// admin and commander agree on what a scout's state is. Keep the two in step.
import { tutToDate } from "../util";
import { OfflineConfig } from "./model/organisation";
import { Scout, ScoutState, TimeState } from "./model/scout";

export const SCOUT_OFFLINE_AFTER_MS = 60 * 1000 * 30;
export const SCOUT_OFFLINE_MIN_MS = 60 * 1000 * 45;
export const SCOUT_OFFLINE_FOR_A_WEEK_MS = 60 * 1000 * 60 * 24 * 7;

/** The thresholds actually used to decide state, after the organisation config and the show-offline setting. */
export interface ResolvedOfflineThresholds {
  /** Not heard from for this long: ScoutState.Offline */
  offlineAfterMs: number;
  /** Not heard from for this long: ScoutState.OfflineIgnore */
  hideAfterMs: number;
}

export const DEFAULT_OFFLINE_THRESHOLDS: ResolvedOfflineThresholds = {
  offlineAfterMs: SCOUT_OFFLINE_AFTER_MS,
  hideAfterMs: SCOUT_OFFLINE_MIN_MS,
};

// The organisation document is read straight from Firestore without validation,
// so anything that isn't a usable number falls back to the default.
const secondsToMillisOr = (seconds: number | undefined, fallback: number): number => {
  if (typeof seconds !== "number" || !isFinite(seconds) || seconds <= 0) {
    return fallback;
  }
  return seconds * 1000;
};

/**
 * @param showOfflineScouts Commander's "Display offline scouts" setting; the admin's "Show offline" toggle.
 * Keeps offline scouts around for a week regardless of the organisation config, which only adjusts
 * how long they stay when the setting is off.
 */
export const resolveOfflineThresholds = (
  offline: OfflineConfig | undefined,
  showOfflineScouts: boolean
): ResolvedOfflineThresholds => {
  const offlineAfterMs = secondsToMillisOr(offline?.sec, SCOUT_OFFLINE_AFTER_MS);
  const hideAfterMs = showOfflineScouts
    ? SCOUT_OFFLINE_FOR_A_WEEK_MS
    : secondsToMillisOr(offline?.hideAfterSec, SCOUT_OFFLINE_MIN_MS);
  return {
    offlineAfterMs,
    // A scout must never be hidden before it has been shown as offline
    hideAfterMs: Math.max(hideAfterMs, offlineAfterMs),
  };
};

export const getPositiveDiffMs = (earlierMs: number) => Math.max(1, Date.now() - earlierMs);

export const getDiffMsSinceLastSeen = (scout: Scout) => {
  // Unlike commander's model, admin Firestore data still has scout documents without a status;
  // they fall through to OfflineIgnore below.
  const tut = scout.status?.receivedTut;
  const lastSeen = tut ? tutToDate(tut) : undefined;
  return lastSeen === undefined ? undefined : getPositiveDiffMs(lastSeen.getTime());
};

function isInaccurateLocation(scout: Scout) {
  // A scout without a location no longer reaches us with scoutLocation === null: the
  // V1/V2 status pipeline (scout-api -> message processors) turns it into a synthetic
  // location at (0, 0), so Null Island has to count as "no location" as well
  return (
    scout.status.scoutLocation === undefined ||
    scout.status.scoutLocation === null ||
    (scout.status.latitude === 0 && scout.status.longitude === 0)
  );
}

// Commander only surfaces "bad location" to the public app; the admin sees every disabled reason.
function isDisabled(scout: Scout): boolean {
  return scout.status.detector?.disabled !== undefined && scout.status.detector?.disabled !== null;
}

function isInitializing(scout: Scout) {
  return (
    scout.status.latitude === undefined ||
    scout.status.latitude === null ||
    scout.status.longitude === undefined ||
    scout.status.longitude === null ||
    scout.status.timeState !== TimeState.Ready ||
    scout.status.model === "None"
  );
}

function isDownloadingModels(scout: Scout) {
  return !!scout.status.downloading;
}

// Statuses from apps older than modelsFailed only carry modelsAvailable; they cannot tell
// "still loading" from "given up".
function reportsModelsFailed(scout: Scout) {
  return scout.status.modelsFailed !== undefined;
}

function modelsNotAvailable(scout: Scout) {
  return scout.status.modelsAvailable !== undefined && !scout.status.modelsAvailable && scout.status.model !== "None";
}

// A required detection model has used up its download attempts; the scout needs a restart.
// For older apps keep today's reading, "not available and not downloading" is unavailable, so
// the fleet looks as it does now until each device updates.
function detectionHasFailed(scout: Scout) {
  return reportsModelsFailed(scout)
    ? scout.status.modelsFailed === true
    : modelsNotAvailable(scout) && !isDownloadingModels(scout);
}

// Models are on the device but not loaded yet: decrypting, or waiting for a decryption key.
// Only apps that report modelsFailed can be trusted to mean "still coming" here.
function detectionIsPreparing(scout: Scout) {
  return reportsModelsFailed(scout) && modelsNotAvailable(scout);
}

export const getStateFor = (
  scout: Scout,
  thresholds: ResolvedOfflineThresholds = DEFAULT_OFFLINE_THRESHOLDS
): ScoutState => {
  const diffMs = getDiffMsSinceLastSeen(scout);
  const notHeardFromInVeryLong = diffMs === undefined || diffMs >= thresholds.hideAfterMs;
  const notHeardFromInAWhile = diffMs !== undefined && diffMs >= thresholds.offlineAfterMs;
  return notHeardFromInVeryLong || scout.config?.hidden
    ? ScoutState.OfflineIgnore
    : notHeardFromInAWhile
      ? ScoutState.Offline
      : // Same precedence as the Scout app: failed needs someone to act, downloading is the long
        // step worth showing even before time and location, preparing is short and sits behind them.
        detectionHasFailed(scout)
        ? ScoutState.DetectionUnavailable
        : isDownloadingModels(scout)
          ? ScoutState.DownloadingDetector
          : isInitializing(scout)
            ? ScoutState.Initializing
            : detectionIsPreparing(scout)
              ? ScoutState.PreparingDetection
              : !scout.status.listening
                ? ScoutState.Ready
                : isInaccurateLocation(scout)
                  ? ScoutState.InaccurateLocation
                  : scout.status.hibernate
                    ? ScoutState.Hibernate
                    : isDisabled(scout)
                      ? ScoutState.Disabled
                      : ScoutState.Listening;
};

export const isOfflineState = (state: ScoutState) => state === ScoutState.Offline || state === ScoutState.OfflineIgnore;

/**
 * For the organisation overview a scout is "listening" unless it is offline, still synchronizing or muted.
 * Auto-muted, hibernating, downloading and location problems are listening scouts with a detail, which
 * the device list shows per row.
 */
export const countsAsListening = (state: ScoutState) =>
  !isOfflineState(state) && state !== ScoutState.Initializing && state !== ScoutState.Ready;

export const changeDoubleToIntegerInString = (str?: string): string | undefined => {
  if (!str) {
    return str;
  }
  return str.replace(/(\d+(\.\d+)?)/, (match) => parseInt(match, 10).toString());
};

/** Commander's wording for each state, so operators read the same thing in both apps. */
export const getScoutStateDescription = (scout: Scout, state: ScoutState = scout.state): string => {
  switch (state) {
    case ScoutState.Offline:
    case ScoutState.OfflineIgnore:
      return "Offline";
    case ScoutState.Ready:
      return "Muted";
    case ScoutState.Listening:
      return "Listening";
    case ScoutState.Hibernate:
      return "Hibernating";
    case ScoutState.Initializing:
      return "Synchronizing";
    case ScoutState.InaccurateLocation:
      return "No location";
    case ScoutState.DownloadingDetector:
      return "Downloading gunshot detection";
    case ScoutState.PreparingDetection:
      return "Preparing gunshot detection";
    case ScoutState.DetectionUnavailable:
      return "Gunshot detection unavailable, restart the app";
    case ScoutState.Disabled:
      return `${scout.status.detector?.disabled?.metric} ${changeDoubleToIntegerInString(scout.status.detector?.disabled?.value)}`;
    default:
      return "Unknown state";
  }
};

export type ScoutStateColor = "green" | "blue" | "red" | "amber" | "disabled";

/**
 * Badge colour per state: green listening, blue muted, grey offline. Amber for auto-muted (Disabled) and
 * the short-lived in-progress states, red for the ones that need attention: synchronizing, which should
 * not persist, and detection unavailable, which needs a restart.
 */
export const scoutStateColor = (state: ScoutState): ScoutStateColor | undefined => {
  switch (state) {
    case ScoutState.Listening:
      return "green";
    case ScoutState.Ready:
      return "blue";
    case ScoutState.Disabled:
    case ScoutState.DownloadingDetector:
    case ScoutState.PreparingDetection:
    case ScoutState.InaccurateLocation:
      return "amber";
    case ScoutState.Initializing:
    case ScoutState.DetectionUnavailable:
      return "red";
    case ScoutState.Hibernate:
    case ScoutState.Offline:
    case ScoutState.OfflineIgnore:
      return "disabled";
    default:
      return undefined;
  }
};

/** Table sort order: working scouts first, then the ones needing attention, offline last. */
const STATE_ORDER: ScoutState[] = [
  ScoutState.Listening,
  ScoutState.Ready,
  ScoutState.Hibernate,
  ScoutState.PreparingDetection,
  ScoutState.DownloadingDetector,
  ScoutState.Initializing,
  ScoutState.InaccurateLocation,
  ScoutState.Disabled,
  ScoutState.DetectionUnavailable,
  ScoutState.Offline,
  ScoutState.OfflineIgnore,
];

export const stateSortOrder = (state: ScoutState) => {
  const index = STATE_ORDER.indexOf(state);
  return index === -1 ? STATE_ORDER.length : index;
};
